import {
  Box,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  Typography,
  useTheme
} from '@mui/material';
import { useState } from 'react';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import LanguageAutocomplete from 'src/components/LanguageAutocomplete';
import SelectDateRangeRetail from 'src/components/Retail/SelectDateRangeRetail';
import TableSelectGroupPortal from 'src/components/TableSelectGroupPortal';
import TooltipCustomize from 'src/components/TooltipCustomize';
import { reducerUpdateAdvanceSearchOfmMediaList } from 'src/features/advanceSearchOfflineMedia';
import { toggleSelectedGroupPortalIdOfm } from 'src/features/groupPortal';
import {
  reducerRefreshOfflineMediaClipping,
  reducerUpdateLanguageSelected
} from 'src/features/offlineMediaStream';
import { useFiltersByName } from 'src/hooks/useFiltersByName';
import { IGroupPortal } from 'src/models/groupPortal';
import RefreshIcon from '@mui/icons-material/Refresh';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

const BottomHeaderOfmClippingById = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const [openSelectGroup, setOpenSelectGroup] = useState(false);
  const { groupPortalList, selectedGroupPortalIdOfm } = useAppSelector(
    (state) => state.groupPortal
  );
  const { languages, languageSelected } = useAppSelector(
    (state) => state.storeOfflineMediaStream
  );

  const { filteredList } = useFiltersByName<IGroupPortal>(groupPortalList.data);

  const handleClickRefresh = () => {
    dispatch(reducerRefreshOfflineMediaClipping());
  };

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      mb={1}
      flexWrap="wrap"
      gap={2}
      alignItems="center"
    >
      {/* Group selection section */}
      <FormControl sx={{ flexBasis: '300px' }}>
        <Select
          sx={{ borderRadius: '32px' }}
          name="group"
          open={openSelectGroup}
          onClose={() => {
            setOpenSelectGroup(false);
            dispatch(
              reducerUpdateAdvanceSearchOfmMediaList(
                filteredList
                  .filter((el) => selectedGroupPortalIdOfm.includes(el.id))
                  .flatMap((el) => el.portals)
              )
            );
          }}
          onOpen={() => setOpenSelectGroup(true)}
          value={'always-display'}
        >
          <MenuItem value={'always-display'} sx={{ display: 'none' }}>
            {filteredList
              .filter((el) => selectedGroupPortalIdOfm.includes(el.id))
              .map((el) => el.displayName)
              .join(',') || 'Group'}
          </MenuItem>
          <Box sx={{ p: theme.spacing(2) }}>
            <Typography variant="h4" sx={{ mb: 1 }}>
              Group
            </Typography>
            <TableSelectGroupPortal
              groupPortals={filteredList}
              selectedGroupByIds={selectedGroupPortalIdOfm}
              loading={groupPortalList.loading}
              onClick={(group) => {
                dispatch(toggleSelectedGroupPortalIdOfm(group.id));
              }}
            />
          </Box>
        </Select>
      </FormControl>

      {/* Select language, timeframe and refresh button section */}
      <Stack direction="row" spacing={1} alignItems="center">
        <FormControl size="small" sx={{ width: 170 }}>
          <SelectDateRangeRetail
            selectProps={{
              size: 'small',
              sx: {
                borderRadius: '8px',
                bgcolor: 'white',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.grey[400]
                },
                '&:hover': {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main
                  },
                  '& .MuiSelect-select': {
                    color: theme.palette.primary.main
                  },
                  '& .MuiSvgIcon-root': {
                    color: theme.palette.primary.main
                  }
                },
                height: 40,
                '& .MuiSelect-select': {
                  color: theme.palette.grey[800],
                  fontWeight: 500
                },
                '& .MuiSvgIcon-root': {
                  color: theme.palette.grey[600]
                }
              },
              startAdornment: (
                <InputAdornment position="start">
                  <CalendarTodayIcon
                    fontSize="small"
                    sx={{ color: 'inherit', ml: 1 }}
                  />
                </InputAdornment>
              )
            }}
          />
        </FormControl>
        <LanguageAutocomplete
          languages={languages}
          languageSelected={languageSelected}
          onChange={(newValue) =>
            dispatch(
              reducerUpdateLanguageSelected(
                newValue?.language || 'all-language'
              )
            )
          }
        />
        <TooltipCustomize title="Refresh" placement="top">
          <IconButton
            onClick={handleClickRefresh}
            sx={{
              bgcolor: 'white',
              border: `1px solid ${theme.palette.grey[400]}`,
              borderRadius: '8px',
              height: 40,
              width: 40,
              '&:hover': {
                bgcolor: 'rgba(0, 159, 227, 0.04)',
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main
              }
            }}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </TooltipCustomize>
      </Stack>
    </Stack>
  );
};

export default BottomHeaderOfmClippingById;
